// src/App.js — CLEAN ROLE-BASED ROUTING
// Landing page → role selection → correct dashboard
// No more shared nav showing everything to everyone

import React, { useCallback, useState } from 'react';
import { AuthProvider, useAuth } from './context/AuthContext';
import { CartProvider }          from './context/CartContext';
import { MultiCartProvider }     from './context/MultiCartContext';
import { LanguageProvider }      from './context/LanguageContext';
import LandingPage               from './pages/LandingPage';
import QRKioskPage               from './pages/QRKioskPage';
import QRMallKioskPage           from './pages/QRMallKioskPage';
import MallKioskPage             from './pages/mallkiosk/MallKioskPage';
import VendorSubscribePage       from './pages/VendorSubscribePage';
import VendorApp                 from './pages/VendorApp';
import MallApp                   from './pages/MallApp';
import SuperAdminApp             from './pages/SuperAdminApp';
import ForgotPasswordPage        from './pages/ForgotPasswordPage';
import ResetPasswordPage         from './pages/ResetPasswordPage';
import AcceptInvitePage          from './pages/AcceptInvitePage';
import StaffApp                  from './pages/StaffApp';
import ChangePasswordPage        from './pages/ChangePasswordPage';
import './index.css';

// ── Root router — picks dashboard by role ─────────────────────
function AppRouter() {
  const { role, user, loading } = useAuth();

  // ── PUBLIC ROUTES — no auth required ─────────────────────
  const currentPath = window.location.pathname;
  if (currentPath.startsWith('/order/vendor/'))  return <QRKioskPage />;
  if (currentPath.startsWith('/order/mall/'))    return <QRMallKioskPage />;
  if (currentPath.startsWith('/kiosk/mall/'))    return <MallKioskPage />;
  if (currentPath === '/vendor/subscribe')       return <VendorSubscribePage />;
  if (currentPath === '/forgot-password')        return <ForgotPasswordPage />;
  if (currentPath === '/reset-password')         return <ResetPasswordPage />;
  if (currentPath === '/accept-invite')          return <AcceptInvitePage />;
  if (currentPath === '/staff')                  return <StaffApp />;

  if (loading) return (
    <div style={{ minHeight:'100vh', display:'flex', alignItems:'center', justifyContent:'center', background:'#0a0f1e' }}>
      <div style={{ color:'#64748B', fontSize:14 }}>Loading...</div>
    </div>
  );

  // Not logged in — show landing page
  if (!role) return <LandingPage />;

  // Temporary password set by an admin — must be changed before anything else
  if ((role === 'VENDOR' || role === 'MALL_ADMIN') && user?.mustChangePassword) return <ChangePasswordPage />;

  // Route to correct dashboard
  if (role === 'VENDOR')     return <VendorApp />;
  if (role === 'MALL_ADMIN')  return <MallApp />;
  if (role === 'SUPER_ADMIN') return <SuperAdminApp />;

  // Fallback
  return <LandingPage />;
}

// ── Provider tree ─────────────────────────────────────────────
// MultiCartProvider — never resets (mall kiosk needs persistent cart)
// CartProvider      — keyed to vendor, resets on vendor change
// AuthProvider      — outside cart so it can trigger cart reset

function AppWithAuth() {
  const [vendorKey, setVendorKey] = useState('initial');

  const handleVendorChange = useCallback(() => {
    setVendorKey('vendor-' + Date.now());
  }, []);

  return (
    <AuthProvider onVendorChange={handleVendorChange}>
      <MultiCartProvider>
        <CartProvider key={vendorKey}>
          <AppRouter />
        </CartProvider>
      </MultiCartProvider>
    </AuthProvider>
  );
}

export default function App() {
  return (
    <LanguageProvider>
      <AppWithAuth />
    </LanguageProvider>
  );
}
